import { useCallback, useMemo } from 'react'
import type { ContentType } from '@/types/Content'
import type { SectionId } from '@/components/content-manager/ContentSidebar'
import type { UseContentManagerConfirmationsReturn } from '@/pages/content-manager'
import type { UseContentManagerActionsReturn } from '@/pages/content-manager'

/**
 * Content item interface matching what's displayed in cards
 * Note: id is string to support both playbook integer IDs (as strings) and block UUIDs
 */
export interface ContentItem {
	id: string
	name: string
	type: ContentType
	playCount?: number
	slideCount?: number
	sectionCount?: number
	updatedAt: string
	starred?: boolean
}

/**
 * Card props that are common between list and grid views
 *
 * PERFORMANCE NOTE: Callbacks accept item ID so we can pass stable function
 * references. The caller (ContentCard) passes the ID when invoking.
 * Note: id is string to support both playbook integer IDs (as strings) and block UUIDs
 */
export interface CommonCardProps {
	id: string
	name: string
	type: ContentType
	playCount?: number
	slideCount?: number
	sectionCount?: number
	lastModified: string
	isStarred?: boolean
	selected: boolean
	onSelect: (id: string, type: ContentType) => void
	onRename: (id: string) => void
	onDelete: (id: string) => void
	onDuplicate?: (id: string) => void
	onExport?: (id: string) => void
	onShare: (id: string) => void
	onToggleStar: (id: string) => void
}

/**
 * Parameters for the card callbacks hook
 */
export interface UseContentCardCallbacksParams {
	activeSection: SectionId
	/** All content items - used for item lookup by ID */
	items: ContentItem[]
	confirmations: UseContentManagerConfirmationsReturn
	actions: Pick<UseContentManagerActionsReturn, 'handleRenamePrompt' | 'handleShare' | 'handleToggleStar'>
	isItemSelected: (id: string, type: ContentType) => boolean
	toggleSelection: (id: string, type: ContentType) => void
}

/**
 * Hook for managing content card callbacks
 *
 * Eliminates duplication between list and grid view renderCard callbacks.
 * Provides a unified getCardProps function that handles all the conditional logic.
 *
 * PERFORMANCE: Uses Map-based item lookup to create stable handler references.
 * Instead of creating closures per item (which defeats React.memo), we:
 * 1. Create handlers that accept item ID
 * 2. Look up item data when handler is called
 * 3. Pass stable function references to cards
 */
export function useContentCardCallbacks({
	activeSection,
	items,
	confirmations,
	actions,
	isItemSelected,
	toggleSelection,
}: UseContentCardCallbacksParams) {
	/**
	 * Map for O(1) item lookup by ID
	 * Memoized to only rebuild when items array changes
	 */
	const itemLookup = useMemo(() => {
		const map = new Map<string, ContentItem>()
		for (const item of items) {
			map.set(item.id, item)
		}
		return map
	}, [items])

	/**
	 * Stable rename handler - looks up item by ID when called
	 * In trash: triggers restore confirmation
	 * Otherwise: triggers rename prompt
	 */
	const handleRename = useCallback((id: string) => {
		const item = itemLookup.get(id)
		if (!item) return

		if (activeSection === 'trash') {
			confirmations.restoreConfirm.prompt({
				id: item.id,
				name: item.name,
				type: item.type,
			})
		} else {
			actions.handleRenamePrompt(item.id, item.name)
		}
	}, [itemLookup, activeSection, confirmations.restoreConfirm, actions.handleRenamePrompt])

	/**
	 * Stable delete handler - looks up item by ID when called
	 * In trash: triggers permanent delete confirmation
	 * Otherwise: triggers soft delete confirmation
	 */
	const handleDelete = useCallback((id: string) => {
		const item = itemLookup.get(id)
		if (!item) return

		if (activeSection === 'trash') {
			confirmations.permanentDeleteConfirm.prompt({
				id: item.id,
				name: item.name,
				type: item.type,
			})
		} else {
			confirmations.deleteConfirm.prompt({
				id: item.id,
				name: item.name,
				type: item.type,
			})
		}
	}, [itemLookup, activeSection, confirmations.permanentDeleteConfirm, confirmations.deleteConfirm])

	/**
	 * Build common props object for a content item
	 *
	 * IMPORTANT: All callback props are stable references.
	 * The item-specific data is passed when the callback is invoked,
	 * not captured in a closure.
	 */
	const getCardProps = useCallback((item: ContentItem): CommonCardProps => {
		return {
			id: item.id,
			name: item.name,
			type: item.type,
			playCount: item.playCount,
			slideCount: item.slideCount,
			sectionCount: item.sectionCount,
			lastModified: new Date(item.updatedAt).toLocaleDateString(),
			isStarred: item.starred,
			selected: isItemSelected(item.id, item.type),
			// All callbacks are stable references
			onSelect: toggleSelection,
			onRename: handleRename,
			onDelete: handleDelete,
			// onDuplicate and onExport are not yet implemented
			onShare: actions.handleShare,
			onToggleStar: actions.handleToggleStar,
		}
	}, [
		isItemSelected,
		toggleSelection,
		handleRename,
		handleDelete,
		actions.handleShare,
		actions.handleToggleStar,
	])

	return {
		getCardProps,
		handleRename,
		handleDelete,
	}
}

export type UseContentCardCallbacksReturn = ReturnType<typeof useContentCardCallbacks>
